// src/components/RequireAuth.js
//
// Route guard for the staff app. A user may enter only if BOTH:
//   1. They are signed in to Firebase (password step), AND
//   2. They have a valid, unexpired otp_sessions/{uid} record — which is only
//      written server-side by verifyOtp after a correct OTP.
//
// This is the UI half of the server-enforced 2FA. The matching Firestore
// Security Rules enforce the same otp_sessions check at the data layer, so a
// password-only session (no OTP) can neither see protected pages nor read data.
//
// Optional `roles` restricts access to specific user_roles values.

import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { Navigate } from "react-router-dom";
import {
  deriveAccess,
  normalizeMembership,
  effectiveRole,
} from "../services/membershipService";
import { getActiveTenantId, getActiveSiteId } from "../services/activeTenant";

const RequireAuth = ({ children, roles }) => {
  const [status, setStatus] = useState("loading"); // loading | ok | denied
  const roleKey = (roles || []).join(",");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setStatus("denied");
        return;
      }
      try {
        // 2FA session must exist and be unexpired.
        const sessionSnap = await getDoc(doc(db, "otp_sessions", user.uid));
        const session = sessionSnap.exists() ? sessionSnap.data() : null;
        const otpValid =
          session && session.expiresAt && Date.now() < session.expiresAt;
        if (!otpValid) {
          setStatus("denied");
          return;
        }

        // Optional role restriction. The role is resolved for the ACTIVE
        // tenant/site via the canonical effectiveRole resolver: owners pass
        // everything, tenant admins count as "admin" within their tenant, and
        // everyone else is judged strictly by their site membership. The legacy
        // flat role can no longer override an explicit site role (site
        // isolation), but unmigrated users still work because deriveAccess folds
        // their legacy role into a default-site membership.
        if (roleKey) {
          const allowed = roleKey.split(",");
          const roleSnap = await getDoc(
            doc(db, "user_roles", (user.email || "").toLowerCase())
          );
          const access = deriveAccess(
            normalizeMembership(roleSnap.exists() ? roleSnap.data() : null)
          );
          const role = effectiveRole(
            access,
            getActiveTenantId(),
            getActiveSiteId()
          );
          const passes =
            role === "owner" || (!!role && allowed.includes(role));
          if (!passes) {
            setStatus("denied");
            return;
          }
        }

        setStatus("ok");
      } catch (e) {
        console.error("Auth guard error:", e);
        setStatus("denied");
      }
    });
    return () => unsub();
  }, [roleKey]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0b0820] text-slate-400 text-sm">
        Verifying access…
      </div>
    );
  }
  if (status === "denied") return <Navigate to="/login" replace />;
  return children;
};

export default RequireAuth;
